import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheckCircle } from '@sorare/core/src/atoms/icons/system/iconCheckCircle.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import {
  RewardRange,
  RewardRangeVariant,
} from '#src/components/leaderboard/RewardRange/index.tsx';
import { ClickableAnyRewardConfig } from '#src/components/rewards/ClickableAnyRewardConfig/index.tsx';
import { isFixtureOpened } from '#src/lib/so5.ts';

import type {
  ScoreRewards_so5League,
  ScoreRewards_so5Ranking,
  ScoreRewards_so5RewardConfig,
} from './__generated__/index.graphql.ts';

const Container = styled.div`
  &.highlightSelected {
    opacity: 0.5;
    &.selected {
      opacity: 1;
    }
  }
`;

type Props = {
  so5RewardConfigs: ScoreRewards_so5RewardConfig[];
  defaultRarity: Rarity | null;
  so5League: ScoreRewards_so5League;
  so5Ranking?: ScoreRewards_so5Ranking;
  targetable?: boolean;
  hideCompletionStatus?: boolean;
  displayAlternativeRewards?: boolean;
} & Pick<ComponentProps<typeof RewardRange>, 'withSeparator'>;

export const ScoreRewards = ({
  so5RewardConfigs,
  defaultRarity,
  so5League,
  so5Ranking,
  targetable,
  hideCompletionStatus,
  withSeparator,
  displayAlternativeRewards,
}: Props) => {
  const isUpcoming = isFixtureOpened(so5League.so5Fixture);

  const sortedSo5RewardConfigs = so5RewardConfigs
    .filter(scoreReward => scoreReward.score)
    .filter(scoreReward =>
      targetable ? !!scoreReward.target : !scoreReward.target
    )
    .sort((a, b) => (b.score || 0) - (a.score || 0));

  const myScore = so5Ranking?.score;
  const myEligibleRewardScore = so5Ranking?.eligibleRewards?.reduce(
    (highest, reward) => {
      if (typeof reward.score === 'number' && reward.score > highest) {
        return reward.score;
      }
      return highest;
    },
    0
  );

  if (!sortedSo5RewardConfigs.length) {
    return null;
  }
  return (
    <Vertical gap={2}>
      {sortedSo5RewardConfigs.map(so5RewardConfig => {
        const selectedTarget = targetable
          ? so5Ranking?.targetedRewardWithEligibility?.reward.target?.slug ===
            so5RewardConfig.target?.slug
          : true;

        const isMyRange =
          typeof myScore === 'number' &&
          myEligibleRewardScore === so5RewardConfig.score &&
          myScore > so5RewardConfig.score;

        return (
          <Container
            key={so5RewardConfig.score}
            className={classNames({
              highlightSelected: !!so5Ranking,
              selected: !targetable || (targetable && selectedTarget),
            })}
          >
            <RewardRange
              variant={
                isUpcoming ? RewardRangeVariant.ROW : RewardRangeVariant.BLOCK
              }
              title={
                <Horizontal>
                  {!!targetable && (
                    <img
                      src={so5RewardConfig.target?.pictureUrl}
                      alt=""
                      width={16}
                      height={16}
                    />
                  )}
                  {!targetable && !isUpcoming && !hideCompletionStatus && (
                    <FontAwesomeIcon
                      icon={iconCheckCircle}
                      color={
                        isMyRange
                          ? 'var(--c-onBase-success)'
                          : 'var(--c-nd-600)'
                      }
                    />
                  )}
                  <BodyS as="p">
                    <FormattedMessage {...fantasy.score} /> {'≥'}{' '}
                    {so5RewardConfig.score}
                  </BodyS>
                </Horizontal>
              }
              rewards={(displayAlternativeRewards
                ? so5RewardConfig.alternativeRewardConfigs
                : so5RewardConfig.rewardConfigs
              ).map((rewardConfig, i) => (
                <ClickableAnyRewardConfig
                  // oxlint-disable-next-line react/no-array-index-key
                  key={i}
                  anyRewardConfig={rewardConfig}
                  rarity={defaultRarity || undefined}
                  so5League={so5League}
                />
              ))}
              withSeparator={withSeparator}
            />
          </Container>
        );
      })}
    </Vertical>
  );
};

ScoreRewards.fragments = {
  so5RewardConfig: gql`
    fragment ScoreRewards_so5RewardConfig on So5RewardConfig {
      id
      score
      target {
        slug
        pictureUrl
      }
      alternativeRewardConfigs {
        id
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
      rewardConfigs {
        id
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ScoreRewards_so5RewardConfig>,
  so5League: gql`
    fragment ScoreRewards_so5League on So5League {
      slug
      so5Fixture {
        slug
        aasmState
      }
      ...ClickableAnyRewardConfig_so5League
    }
    ${ClickableAnyRewardConfig.fragments.so5League}
  ` as TypedDocumentNode<ScoreRewards_so5League>,
  so5Ranking: gql`
    fragment ScoreRewards_so5Ranking on So5Ranking {
      id
      score
      eligibleRewards {
        id
        score
      }
      targetedRewardWithEligibility {
        reward {
          id
          target {
            slug
          }
        }
      }
    }
  ` as TypedDocumentNode<ScoreRewards_so5Ranking>,
};
